import { useMemo, useState, ChangeEvent } from 'react';

export interface PaginationControlProps {
  component: 'div';
  count: number;
  page: number;
  onPageChange: (event: unknown, newPage: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions: number[];
}

/**
 * Client-side pagination for tables whose full dataset is already loaded in memory (every
 * table in this app fetches its whole list in one call, there's no server-side paging today).
 * Slices `rows` to the current page and hands back props ready to spread onto MUI's
 * <TablePagination {...paginationProps} />. Defaults to 5 rows per page.
 *
 * The current page is clamped back to a valid range whenever it falls off the end of a
 * shorter list (reload, filter, search) instead of unconditionally resetting to 0 on every
 * render — so paging doesn't jump on unrelated re-renders, only when it actually must.
 */
export function usePagination<T>(rows: T[], defaultRowsPerPage = 5) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  const pagedRows = useMemo(
    () => rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [rows, safePage, rowsPerPage]
  );

  const paginationProps: PaginationControlProps = {
    component: 'div',
    count: rows.length,
    page: safePage,
    onPageChange: (_event, newPage) => setPage(newPage),
    rowsPerPage,
    onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    rowsPerPageOptions: [5, 10, 25],
  };

  return { pagedRows, paginationProps };
}
